import React from 'react';
import { renderCell, renderCellDate } from '../utils/helpers';
import {
  EQUIPMENT_FIELD_DEFS,
  formatEquipmentFieldPlain,
  getEquipmentDisplayEntries,
  normalizeEquipmentRecord,
  storedEquipmentStatusLabel,
} from '../utils/equipmentDisplay';
import { EQUIPMENT_TYPE_LABELS } from '../utils/constants';

function renderFormattedValue(record, def) {
  const norm = normalizeEquipmentRecord(record);
  const val = norm[def.key] ?? (def.altKeys && def.altKeys.map((k) => norm[k]).find(Boolean));
  if (def.format === 'date') return renderCellDate(val);
  if (def.format === 'equipmentType') {
    return renderCell(EQUIPMENT_TYPE_LABELS[val] || val);
  }
  if (def.format === 'storedStatus') {
    return renderCell(storedEquipmentStatusLabel(val));
  }
  return renderCell(formatEquipmentFieldPlain(norm, def) === '—' ? null : val);
}

/**
 * Full equipment field list — always shows every label (empty → —).
 */
const EquipmentDetailSummary = ({
  equipment,
  className = '',
  columns = 2,
  dense = false,
  excludeKeys = [],
  omitEmpty = false,
  forReport = false,
}) => {
  if (!equipment) return null;
  const norm = normalizeEquipmentRecord(equipment);
  const defs = EQUIPMENT_FIELD_DEFS.filter((d) => !excludeKeys.includes(d.key));
  const gridClass =
    columns === 1
      ? 'grid grid-cols-1 gap-3'
      : columns === 3
        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
        : 'grid grid-cols-1 sm:grid-cols-2 gap-3';

  if (dense) {
    const entries = getEquipmentDisplayEntries(norm, {
      includeEmpty: !omitEmpty,
      forReport: forReport || omitEmpty,
    }).filter(
      (e) => !excludeKeys.includes(e.key)
    );
    return (
      <dl className={`${gridClass} text-sm ${className}`}>
        {entries.map(({ key, label, plain }) => (
          <div key={key}>
            <dt className="font-semibold text-gray-700">{label}</dt>
            <dd className="text-gray-900 whitespace-pre-wrap mt-0.5">{plain}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={`${gridClass} text-sm ${className}`}>
      {defs.map((def) => (
        <div key={def.key}>
          <dt className="font-semibold text-gray-700">{def.label}</dt>
          <dd className="text-gray-900 whitespace-pre-wrap mt-0.5">{renderFormattedValue(norm, def)}</dd>
        </div>
      ))}
    </dl>
  );
};

export default EquipmentDetailSummary;
